/**
 * ArticlesStore — المخزن المركزي للمقالات
 * يقرأ ويكتب من Supabase مباشرة
 * مع in-memory cache لتقليل الطلبات المتكررة
 */

import { supabase } from "@/lib/supabase";
import { queryCache } from "@/lib/queryCache";
import type { Article } from "@/mocks/allArticles";

export type ArticleStatus = "published" | "featured" | "draft" | "scheduled";

export interface ManagedArticle extends Article {
  status: ArticleStatus;
  updatedAt?: string;
  editorName?: string;
  heroSlider?: boolean;
  breakingNews?: boolean;
  trending?: boolean;
  views_count?: number;
  likes_count?: number;
  scheduledAt?: string | null;
  readAlsoSlugs?: string[];
  readAlsoPosition?: number;
  expiresAt?: string | null;
  pinnedToCategory?: boolean;
  coverImageSettings?: {
    displayMode: "cover" | "contain";
    positionX: number;
    positionY: number;
    zoom: number;
  };
}

const BILINGUAL_LIST_FIELDS = [
  "title_ar", "title_en", "excerpt_ar", "excerpt_en",
].join(", ");

// الحقول الأساسية للقوائم (بدون content الثقيل)
const LIST_FIELDS = [
  "id", "slug", "category", "category_slug", "title", "excerpt",
  "author", "author_title", "author_image", "date", "read_time",
  "views", "image", "thumbnail_image", "tags", "featured", "images",
  "image_placement", "status", "hero_slider", "breaking_news", "trending",
  "views_count", "likes_count", "scheduled_at", "read_also_slugs", "read_also_position",
  "expires_at", "secondary_category", "secondary_category_slug",
  "updated_at", "editor_name", "pinned_to_category", "cover_image_settings",
  BILINGUAL_LIST_FIELDS,
].join(", ");

// حقول مخففة للصفحة الرئيسية (أسرع — بدون حقول ثقيلة)
const HOME_FIELDS = [
  "id", "slug", "category", "category_slug", "title", "excerpt",
  "author", "author_title", "author_image", "date", "read_time",
  "views", "image", "thumbnail_image", "tags", "featured",
  "status", "hero_slider", "breaking_news", "trending",
  "views_count", "expires_at",
  "secondary_category", "secondary_category_slug",
  BILINGUAL_LIST_FIELDS,
].join(", ");

// الحقول الكاملة لصفحة المقال (تشمل content فقط — videos موجودة في LIST_FIELDS)
const FULL_FIELDS = LIST_FIELDS + ", content, content_ar, content_en";

export function localizeArticle(article: ManagedArticle, language: string): ManagedArticle {
  if (language !== "en") {
    return {
      ...article,
      title: article.titleAr || article.title,
      excerpt: article.excerptAr || article.excerpt,
      content: article.contentAr || article.content,
    };
  }

  return {
    ...article,
    title: article.titleEn || article.titleAr || article.title,
    excerpt: article.excerptEn || article.excerptAr || article.excerpt,
    content: article.contentEn || article.contentAr || article.content,
  };
}

function mapRow(row: Record<string, unknown>): ManagedArticle {
  const titleAr = (row.title_ar as string | undefined) || (row.title as string) || "";
  const excerptAr = (row.excerpt_ar as string | undefined) || (row.excerpt as string) || "";
  const contentAr = (row.content_ar as string | undefined) || (row.content as string | undefined);

  return {
    id: row.id as number,
    slug: row.slug as string,
    category: row.category as string,
    categorySlug: row.category_slug as string,
    title: (row.title as string) || titleAr,
    titleAr,
    titleEn: (row.title_en as string | undefined) || "",
    excerpt: (row.excerpt as string) || excerptAr,
    excerptAr,
    excerptEn: (row.excerpt_en as string | undefined) || "",
    content: (row.content as string | undefined) || contentAr,
    contentAr,
    contentEn: (row.content_en as string | undefined) || "",
    author: row.author as string,
    authorTitle: row.author_title as string,
    authorImage: row.author_image as string,
    date: row.date as string,
    readTime: row.read_time as string,
    views: String(row.views ?? ""),
    image: row.image as string,
    tags: (row.tags as string[]) || [],
    featured: row.featured as boolean,
    images: (row.images as Article["images"]) || [],
    videos: (row.videos as Article["videos"]) || [],
    imagePlacement: (row.image_placement as Article["imagePlacement"]) || "bottom",
    status: (row.status as ArticleStatus) || "published",
    updatedAt: row.updated_at as string | undefined,
    editorName: row.editor_name as string | undefined,
    heroSlider: (row.hero_slider as boolean) || false,
    breakingNews: (row.breaking_news as boolean) || false,
    trending: (row.trending as boolean) || false,
    views_count: (row.views_count as number) || 0,
    likes_count: (row.likes_count as number) || 0,
    scheduledAt: (row.scheduled_at as string | null) ?? null,
    readAlsoSlugs: (row.read_also_slugs as string[]) ?? [],
    readAlsoPosition: (row.read_also_position as number) ?? 3,
    expiresAt: (row.expires_at as string | null) ?? null,
    secondaryCategory: (row.secondary_category as string | undefined) ?? undefined,
    secondaryCategorySlug: (row.secondary_category_slug as string | undefined) ?? undefined,
    thumbnailImage: (row.thumbnail_image as string | undefined) ?? undefined,
    pinnedToCategory: (row.pinned_to_category as boolean) || false,
    coverImageSettings: (row.cover_image_settings as ManagedArticle["coverImageSettings"]) ?? undefined,
  };
}

function toDb(article: Partial<ManagedArticle>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (article.slug !== undefined) db.slug = article.slug;
  if (article.category !== undefined) db.category = article.category;
  if (article.categorySlug !== undefined) db.category_slug = article.categorySlug;
  if (article.title !== undefined) db.title = article.title;
  if (article.titleAr !== undefined) db.title_ar = article.titleAr;
  if (article.titleEn !== undefined) db.title_en = article.titleEn;
  if (article.excerpt !== undefined) db.excerpt = article.excerpt;
  if (article.excerptAr !== undefined) db.excerpt_ar = article.excerptAr;
  if (article.excerptEn !== undefined) db.excerpt_en = article.excerptEn;
  if (article.content !== undefined) db.content = article.content;
  if (article.contentAr !== undefined) db.content_ar = article.contentAr;
  if (article.contentEn !== undefined) db.content_en = article.contentEn;
  if (article.author !== undefined) db.author = article.author;
  if (article.authorTitle !== undefined) db.author_title = article.authorTitle;
  if (article.authorImage !== undefined) db.author_image = article.authorImage;
  if (article.date !== undefined) db.date = article.date;
  if (article.readTime !== undefined) db.read_time = article.readTime;
  if (article.views !== undefined) db.views = article.views;
  if (article.image !== undefined) db.image = article.image;
  if (article.tags !== undefined) db.tags = article.tags;
  if (article.featured !== undefined) db.featured = article.featured;
  if (article.images !== undefined) db.images = article.images;
  if (article.videos !== undefined) db.videos = article.videos;
  if (article.imagePlacement !== undefined) db.image_placement = article.imagePlacement;
  if (article.status !== undefined) db.status = article.status;
  if (article.updatedAt !== undefined) db.updated_at = article.updatedAt;
  if (article.editorName !== undefined) db.editor_name = article.editorName;
  if (article.heroSlider !== undefined) db.hero_slider = article.heroSlider;
  if (article.breakingNews !== undefined) db.breaking_news = article.breakingNews;
  if (article.trending !== undefined) db.trending = article.trending;
  if (article.scheduledAt !== undefined) db.scheduled_at = article.scheduledAt || null;
  if (article.secondaryCategory !== undefined) db.secondary_category = article.secondaryCategory || null;
  if (article.secondaryCategorySlug !== undefined) db.secondary_category_slug = article.secondaryCategorySlug || null;
  if (article.readAlsoSlugs !== undefined) db.read_also_slugs = article.readAlsoSlugs;
  if (article.readAlsoPosition !== undefined) db.read_also_position = article.readAlsoPosition;
  if (article.expiresAt !== undefined) db.expires_at = article.expiresAt || null;
  if (article.thumbnailImage !== undefined) db.thumbnail_image = article.thumbnailImage || null;
  if (article.pinnedToCategory !== undefined) db.pinned_to_category = article.pinnedToCategory;
  if (article.coverImageSettings !== undefined) db.cover_image_settings = article.coverImageSettings ?? null;
  return db;
}

function dispatch() {
  // إبطال الكاش عند أي تعديل
  queryCache.invalidate("articles:");
  window.dispatchEvent(new CustomEvent("alsiyahiah:articles:updated"));
}

export const articlesStore = {
  async getAll(limit = 300): Promise<ManagedArticle[]> {
    const cacheKey = `articles:all:${limit}`;
    const cached = queryCache.get<ManagedArticle[]>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from("articles")
      .select(LIST_FIELDS)
      .order("id", { ascending: false })
      .limit(limit);
    if (error) {
      console.error("articlesStore.getAll error:", error);
      return [];
    }
    const result = (data || []).map(mapRow);
    queryCache.set(cacheKey, result);
    return result;
  },

  async getPublished(limit = 20): Promise<ManagedArticle[]> {
    // ⚠️ إعادة توجيه دائماً إلى getPublishedAndFeatured لضمان عدم تسرب البيانات القديمة
    return this.getPublishedAndFeatured(limit, false);
  },

  async getPublishedAndFeatured(limit = 20, skipCache = false): Promise<ManagedArticle[]> {
    const cacheKey = `articles:pubfeat:${limit}`;
    if (!skipCache) {
      const cached = queryCache.get<ManagedArticle[]>(cacheKey, 5_000);
      if (cached) return cached;
    }

    const now = new Date().toISOString();
    const MINIMAL_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, image, thumbnail_image, status, views_count, expires_at, author, read_time, date`;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_FIELDS)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(limit)
        .abortSignal(controller.signal);
      clearTimeout(timer);

      if (error) {
        console.warn("getPublishedAndFeatured error:", error.message);
        return [];
      }

      const result = (data || [])
        .map(mapRow)
        .filter(
          (a) =>
            (!a.expiresAt || new Date(a.expiresAt) > new Date(now))
        )
        .slice(0, limit);

      queryCache.set(cacheKey, result);
      return result;
    } catch (err) {
      if ((err as Error).message === "AbortError") {
        console.warn("getPublishedAndFeatured: query timed out — returning empty");
      } else {
        console.error("getPublishedAndFeatured exception:", err);
      }
      return [];
    }
  },

  async getFeatured(): Promise<ManagedArticle[]> {
    const cached = queryCache.get<ManagedArticle[]>("articles:featured");
    if (cached) return cached;

    const { data, error } = await supabase
      .from("articles")
      .select(LIST_FIELDS)
      .eq("status", "featured")
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .limit(50);
    if (error) {
      console.error("articlesStore.getFeatured error:", error);
      return [];
    }
    const result = (data || []).map(mapRow);
    queryCache.set("articles:featured", result);
    return result;
  },

  async getHeroSlider(): Promise<ManagedArticle[]> {
    const cached = queryCache.get<ManagedArticle[]>("articles:hero");
    if (cached) return cached;

    const now = new Date().toISOString();
    const HERO_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, author, author_title, author_image, date, read_time, views, image, thumbnail_image, status, hero_slider, expires_at, cover_image_settings`;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const { data, error } = await supabase
        .from("articles")
        .select(HERO_FIELDS)
        .eq("hero_slider", true)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(10)
        .abortSignal(controller.signal);
      clearTimeout(timer);

      if (error) {
        console.warn("getHeroSlider query error:", error.message);
        return [];
      }
      const result = (data || [])
        .map(mapRow)
        .filter((a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now))
        .slice(0, 6);
      queryCache.set("articles:hero", result);
      return result;
    } catch {
      return [];
    }
  },

  async getTrending(): Promise<ManagedArticle[]> {
    const cached = queryCache.get<ManagedArticle[]>("articles:trending");
    if (cached) return cached;

    const now = new Date().toISOString();
    const TRENDING_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, author, author_title, author_image, date, read_time, views, image, thumbnail_image, status, trending, views_count, expires_at`;
    const MINIMAL_TRENDING_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, image, thumbnail_image, status, trending, expires_at, author, read_time, date`;

    // المحاولة 1: استعلام مباشر بـ WHERE
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const { data, error } = await supabase
        .from("articles")
        .select(TRENDING_FIELDS)
        .eq("trending", true)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(8)
        .abortSignal(controller.signal);
      clearTimeout(timeout);

      if (!error && data && data.length > 0) {
        const result = (data || []).map(mapRow).filter(
          (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
        ).slice(0, 5);
        queryCache.set("articles:trending", result);
        return result;
      }
    } catch {
      // ignore
    }

    // المحاولة 2: fallback خفيف — أحدث 20 مقال + تصفية client-side
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_TRENDING_FIELDS)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(20)
        .abortSignal(controller.signal);
      clearTimeout(timeout);

      if (error) {
        console.warn("articlesStore.getTrending fallback skipped:", error.message);
        return [];
      }
      const result = (data || [])
        .map(mapRow)
        .filter(
          (a) =>
            a.trending === true &&
            (!a.expiresAt || new Date(a.expiresAt) > new Date(now))
        )
        .slice(0, 5);
      queryCache.set("articles:trending", result);
      return result;
    } catch {
      return [];
    }
  },

  async getMostViewed(limit = 5): Promise<ManagedArticle[]> {
    const cacheKey = `articles:mostviewed:${limit}`;
    const cached = queryCache.get<ManagedArticle[]>(cacheKey);
    if (cached) return cached;

    const now = new Date().toISOString();
    const MINIMAL_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, image, thumbnail_image, status, views_count, expires_at, author, read_time, date`;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_FIELDS)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(limit * 3)
        .abortSignal(controller.signal);
      clearTimeout(timer);

      if (error) {
        console.warn("articlesStore.getMostViewed error:", error.message);
        return [];
      }
      const result = (data || [])
        .map(mapRow)
        .filter(
          (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
        )
        .sort((a, b) => (b.views_count || 0) - (a.views_count || 0))
        .slice(0, limit);
      queryCache.set(cacheKey, result);
      return result;
    } catch (err) {
      if ((err as Error).message === "AbortError") {
        console.warn("getMostViewed: query timed out — returning empty");
      } else {
        console.error("getMostViewed exception:", err);
      }
      return [];
    }
  },

  async getBreakingNews(): Promise<ManagedArticle[]> {
    const cached = queryCache.get<ManagedArticle[]>("articles:breaking");
    if (cached) return cached;

    const now = new Date().toISOString();
    const MINIMAL_BREAKING_FIELDS = `id, title, slug, ${BILINGUAL_LIST_FIELDS}, status, expires_at, breaking_news`;

    // المحاولة 1: WHERE مباشر
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_BREAKING_FIELDS)
        .eq("breaking_news", true)
        .neq("status", "draft")
        .neq("status", "scheduled")
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(15)
        .abortSignal(controller.signal);
      clearTimeout(timer);

      if (!error && data && data.length > 0) {
        const result = (data || []).map(mapRow).filter(
          (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
        ).slice(0, 10);
        queryCache.set("articles:breaking", result);
        return result;
      }
    } catch {
      // ignore
    }

    // المحاولة 2: fallback
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000);
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_BREAKING_FIELDS)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(20)
        .abortSignal(controller.signal);
      clearTimeout(timer);

      if (error) {
        console.warn("articlesStore.getBreakingNews fallback skipped:", error.message);
        return [];
      }
      const result = (data || [])
        .map(mapRow)
        .filter(
          (a) =>
            a.breakingNews === true &&
            (!a.expiresAt || new Date(a.expiresAt) > new Date(now))
        )
        .slice(0, 10);
      queryCache.set("articles:breaking", result);
      return result;
    } catch {
      return [];
    }
  },

  async getBySlug(slug: string): Promise<ManagedArticle | undefined> {
    const cacheKey = `articles:slug:${slug}`;
    const cached = queryCache.get<ManagedArticle>(cacheKey, 60_000); // 60s للمقال الكامل
    if (cached) return cached;

    const { data, error } = await supabase
      .from("articles")
      .select(FULL_FIELDS)
      .eq("slug", slug)
      .maybeSingle();
    if (error || !data) return undefined;
    const result = mapRow(data);
    queryCache.set(cacheKey, result);
    return result;
  },

  async getByCategory(categorySlug: string, limit = 100): Promise<ManagedArticle[]> {
    const cacheKey = `articles:cat:${categorySlug}:${limit}`;
    const cached = queryCache.get<ManagedArticle[]>(cacheKey);
    if (cached) return cached;

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("articles")
      .select(LIST_FIELDS)
      .eq("category_slug", categorySlug)
      .in("status", ["published", "featured"])
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .limit(limit);
    if (error) {
      console.error("articlesStore.getByCategory error:", error);
      return [];
    }
    const result = (data || []).map(mapRow).filter(
      (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
    );
    queryCache.set(cacheKey, result);
    return result;
  },

  /**
   * Get articles where category OR secondary_category matches — used for subcategories
   * so /category/dubai shows articles where category=dubai OR secondary_category=dubai
   */
  async getByCategoryOrSecondary(categorySlug: string, limit = 100): Promise<ManagedArticle[]> {
    const cacheKey = `articles:catsec:${categorySlug}:${limit}`;
    const cached = queryCache.get<ManagedArticle[]>(cacheKey);
    if (cached) return cached;

    const now = new Date().toISOString();
    const MINIMAL_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, image, thumbnail_image, status, views_count, expires_at, author, read_time, date, secondary_category, secondary_category_slug`;

    try {
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_FIELDS)
        .or(`category_slug.eq.${categorySlug},secondary_category_slug.eq.${categorySlug}`)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("articlesStore.getByCategoryOrSecondary error:", error);
        return [];
      }
      const result = (data || []).map(mapRow).filter(
        (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
      );
      queryCache.set(cacheKey, result);
      return result;
    } catch (err) {
      console.error("getByCategoryOrSecondary exception:", err);
      return [];
    }
  },

  /**
   * Get articles where category OR secondary_category matches ANY of the given
   * slugs — a single query that replaces the old per-subcategory N+1 loop.
   */
  async getByCategoriesOrSecondary(categorySlugs: string[], limit = 100): Promise<ManagedArticle[]> {
    const slugs = [...new Set(categorySlugs.filter(Boolean))];
    if (slugs.length === 0) return [];
    if (slugs.length === 1) return this.getByCategoryOrSecondary(slugs[0], limit);

    const cacheKey = `articles:catsecmulti:${slugs.slice().sort().join(",")}:${limit}`;
    const cached = queryCache.get<ManagedArticle[]>(cacheKey);
    if (cached) return cached;

    const now = new Date().toISOString();
    const MINIMAL_FIELDS = `id, slug, category, category_slug, title, excerpt, ${BILINGUAL_LIST_FIELDS}, image, thumbnail_image, status, views_count, expires_at, author, read_time, date, secondary_category, secondary_category_slug`;
    const orExpr = slugs
      .map((s) => `category_slug.eq.${s},secondary_category_slug.eq.${s}`)
      .join(",");

    try {
      const { data, error } = await supabase
        .from("articles")
        .select(MINIMAL_FIELDS)
        .or(orExpr)
        .in("status", ["published", "featured"])
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("articlesStore.getByCategoriesOrSecondary error:", error);
        return [];
      }
      const result = (data || []).map(mapRow).filter(
        (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
      );
      queryCache.set(cacheKey, result);
      return result;
    } catch (err) {
      console.error("getByCategoriesOrSecondary exception:", err);
      return [];
    }
  },

  /**
   * Get articles for a section binding slug.
   * If bindingSlug is empty/falsy, returns all published+featured articles.
   * Otherwise, returns articles matching the given category or secondary category.
   */
  async getArticlesForBinding(bindingSlug: string, limit = 100): Promise<ManagedArticle[]> {
    if (!bindingSlug) {
      return this.getPublishedAndFeatured(limit);
    }
    return this.getByCategoryOrSecondary(bindingSlug, limit);
  },

  async search(query: string): Promise<ManagedArticle[]> {
    const q = query.trim().toLowerCase();
    if (!q) return this.getPublished();

    // البحث لا يُكاش لأنه ديناميكي
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("articles")
      .select(LIST_FIELDS)
      .in("status", ["published", "featured"])
      .or(`title.ilike.%${q}%,title_ar.ilike.%${q}%,title_en.ilike.%${q}%,excerpt.ilike.%${q}%,excerpt_ar.ilike.%${q}%,excerpt_en.ilike.%${q}%,author.ilike.%${q}%,category.ilike.%${q}%`)
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .limit(100);
    if (error) {
      console.error("articlesStore.search error:", error);
      return [];
    }
    return (data || []).map(mapRow).filter(
      (a) => !a.expiresAt || new Date(a.expiresAt) > new Date(now)
    );
  },

  async add(article: ManagedArticle): Promise<void> {
    const { data: existing } = await supabase
      .from("articles")
      .select("id")
      .eq("slug", article.slug)
      .maybeSingle();
    if (existing) {
      article = { ...article, slug: `${article.slug}-${Date.now()}` };
    }
    const db = toDb(article);
    const { error } = await supabase.from("articles").insert(db);
    if (error) {
      console.error("articlesStore.add error:", error);
      return;
    }
    dispatch();
  },

  async update(slug: string, data: Partial<ManagedArticle>): Promise<void> {
    const db = toDb(data);
    db.updated_at = new Date().toLocaleDateString("en-US");
    const { error } = await supabase
      .from("articles")
      .update(db)
      .eq("slug", slug);
    if (error) {
      console.error("articlesStore.update error:", error);
      return;
    }
    dispatch();
  },

  async delete(id: number): Promise<void> {
    const { error } = await supabase.from("articles").delete().eq("id", id);
    if (error) {
      console.error("articlesStore.delete error:", error);
      return;
    }
    dispatch();
  },

  async getScheduled(): Promise<ManagedArticle[]> {
    const { data, error } = await supabase
      .from("articles")
      .select(LIST_FIELDS)
      .eq("status", "scheduled")
      .order("scheduled_at", { ascending: true });
    if (error) {
      console.error("articlesStore.getScheduled error:", error);
      return [];
    }
    return (data || []).map(mapRow);
  },

  async setStatus(id: number, status: ArticleStatus): Promise<void> {
    const { error } = await supabase
      .from("articles")
      .update({
        status,
        featured: status === "featured",
        updated_at: new Date().toLocaleDateString("en-US"),
      })
      .eq("id", id);
    if (error) {
      console.error("articlesStore.setStatus error:", error);
      return;
    }
    dispatch();
  },

  async nextId(): Promise<number> {
    const { data, error } = await supabase
      .from("articles")
      .select("id")
      .order("id", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return 1;
    return (data.id as number) + 1;
  },

  slugify(title: string): string {
    return title
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\u0600-\u06FFa-zA-Z0-9-]/g, "")
      .toLowerCase()
      .slice(0, 80);
  },

  async reset(): Promise<void> {
    queryCache.invalidate("articles:");
    // لا نُطلق dispatch() هنا لمنع loop لا نهائي
  },
};
